import useRightEdgeLauncherPosition from 'hooks/chat/useRightEdgeLauncherPosition';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';
import { IconButton, styled } from 'shell/components';

import {
    LauncherBottomPaddingPx,
    LauncherHeightPx,
    LauncherInitialTopPx,
    LauncherRightOffsetPx,
    LauncherTopPaddingPx,
    LauncherWidthPx,
} from './constants';

const HugoLauncherControl = ({ onOpen, onVerticalPositionChange, verticalPosition }) => {
    const shouldSuppressClickRef = useRef(false);
    const { t } = useTranslation('chat');

    const { position, onDragStop } = useRightEdgeLauncherPosition({
        bottomPadding: LauncherBottomPaddingPx,
        height: LauncherHeightPx,
        initialY: LauncherInitialTopPx,
        onVerticalPositionChange,
        rightOffset: LauncherRightOffsetPx,
        topPadding: LauncherTopPaddingPx,
        verticalPosition,
        width: LauncherWidthPx,
    });

    const handleDrag = useCallback(() => {
        shouldSuppressClickRef.current = true;
    }, []);

    const handleDragStop = useCallback(
        (e, d) => {
            onDragStop(e, d);
            setTimeout(() => {
                shouldSuppressClickRef.current = false;
            }, 50);
        },
        [onDragStop]
    );

    const handleClick = useCallback(
        (event) => {
            if (shouldSuppressClickRef.current) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            onOpen();
        },
        [onOpen]
    );

    return (
        <HugoLauncherViewport>
            <HugoLauncher
                bounds={'parent'}
                dragAxis={'y'}
                dragHandleClassName={'hugo-launcher-drag-handle'}
                enableResizing={false}
                position={position}
                size={{ width: LauncherWidthPx, height: LauncherHeightPx }}
                onDrag={handleDrag}
                onDragStop={handleDragStop}
            >
                <HugoLauncherButton
                    aria-label={t('hugo.open-label', 'Open Hugo Assistant')}
                    className={'hugo-launcher-drag-handle'}
                    kind={'hugo'}
                    size={'large'}
                    variant={'square'}
                    onClick={handleClick}
                />
            </HugoLauncher>
        </HugoLauncherViewport>
    );
};

const HugoLauncherViewport = styled('div')(({ theme }) => ({
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.drawer + 2,
    overflow: 'hidden',
    pointerEvents: 'none',
}));

const HugoLauncher = styled(Rnd)(() => ({
    pointerEvents: 'auto',
}));

const HugoLauncherButton = styled(IconButton)(({ theme }) => ({
    width: '100%',
    height: '100%',
    borderRadius: '10px 0 0 10px',
    background: theme.palette.ensightful.warning[50],
    boxShadow: theme.palette.ensightful.shadows[2],
    color: theme.palette.ensightful.shades[0],
    touchAction: 'none',
    transition: theme.transitions.default,
    border: `1px solid ${theme.palette.ensightful.warning[800]}`,
    borderRight: 'none',
    padding: theme.spacing(1.5),

    '&& > svg': {
        fontSize: '30px',
    },

    '&:active': {
        background: theme.palette.ensightful.warning[200],
        cursor: 'grabbing',
    },
    '&:hover': {
        background: theme.palette.ensightful.warning[100],
        color: theme.palette.ensightful.shades[0],
    },
    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '4px',
    },
    '& svg': {
        fontSize: '24px',
    },
}));

export default HugoLauncherControl;
